import genres from '../genres.json';

export const COUNTER_ID = 90207402;
const GA_ID = 'G-XD6S6MGK7E';
const STORAGE_KEY = 'pod33_acquisition_v1';
const QA_KEY = 'pod33_qa';
const PUBLIC_ORIGIN = 'https://podborknig.ru';
const ROUTES = new Set(['/', '/blog', '/login', '/signup', '/cabinet', '/subscriptions', '/policy', '/terms']);
const UTM = {
    utm_source: ['telegram_bookbotgroup', 'reddit_rubooks', 'internal'],
    utm_medium: ['community', 'agent_qa'],
    utm_campaign: ['pod38_series_pilot', 'pod32_baseline_20261023', 'pod33_qa'],
    utm_content: ['checklist_v1', 'forgotten_author_v1', 'desktop', 'mobile']
};
const NUMERIC_FILTERS = new Set(['limit', 'yearMin', 'yearMax', 'year_min', 'year_max', 'avgRateMin', 'avgRateMax', 'totalGradesMin', 'totalGradesMax', 'total_grades_min', 'total_grades_max']);
const BOOLEAN_FILTERS = new Set(['showOnlySubscribedAuthorsBooks', 'showOnlyBooksWithoutUserGrades']);
const GENRE_KEYS = new Set(['genresMetaInclude', 'genreMetaInclude', 'genresInclude']);
const GENRES = new Set(genres.genres.flatMap(g => [g.value, g.metaName]));
const EVENTS = new Set(['catalog_loaded', 'catalog_error', 'filter_applied', 'author_open', 'follow_author_success', 'signup_success', 'telegram_link_start', 'telegram_code_ready', 'telegram_link_error']);
const OUTCOMES = new Set(['success', 'empty', 'error']);
const ACTIONS = new Set(['initial', 'apply', 'reset', 'search', 'pagination', 'existing_code', 'new_code']);
let initialized = false;
let enabled = false;
let qa = false;
let lastPage = null;
let acquisition = {};
let nativeReplace;

function storageGet(key) {
    try { return sessionStorage.getItem(key); } catch { return null; }
}
function storageSet(key, value) {
    try { sessionStorage.setItem(key, value); } catch { /* Storage is optional. */ }
}

export function routePath(pathname) {
    const path = pathname.replace(/\/+$/, '') || '/';
    if (ROUTES.has(path) || /^\/author\/\d{1,12}$/.test(path)) return path;
    return '/other';
}

function safeUtm(key, value) {
    if (!value) return null;
    return UTM[key].includes(value) ? value : 'other';
}

// Only public filter values and registered campaign labels may remain in the
// real URL: GA4's existing history listener can read it independently of us.
export function cleanBrowserUrl(input) {
    const url = new URL(input, window.location.href);
    const clean = new URL(url.origin + url.pathname);
    for (const [key, value] of url.searchParams) {
        if (UTM[key]) {
            const safe = safeUtm(key, value);
            if (safe !== null) clean.searchParams.set(key, safe);
        }
        else if (NUMERIC_FILTERS.has(key) && /^(?:\d{1,7}(?:\.\d{1,2})?)?$/.test(value)) clean.searchParams.set(key, value);
        else if (BOOLEAN_FILTERS.has(key) && ['true', 'false'].includes(value)) clean.searchParams.set(key, value);
        else if (key === 'orderBy' && ['time_desc', 'time_asc', 'average_desc', 'average_asc', 'total_desc', 'total_asc'].includes(value)) clean.searchParams.set(key, value);
        else if (GENRE_KEYS.has(key) && value.length < 4000 && value.split(/[|,]/).every(v => !v || GENRES.has(v))) clean.searchParams.set(key, value);
    }
    if (url.hash === '#catalog') clean.hash = '#catalog';
    return clean;
}

function safeReferrer(input) {
    if (!input) return '';
    try {
        const url = new URL(input);
        if (url.hostname === 'podborknig.ru') return PUBLIC_ORIGIN + routePath(url.pathname);
        if (['t.me', 'www.reddit.com', 'reddit.com', 'www.google.com', 'google.com', 'yandex.ru', 'www.yandex.ru'].includes(url.hostname)) return url.origin + '/';
        return 'https://other.invalid/';
    } catch { return ''; }
}

function pageUrl(input = window.location.href) {
    const url = new URL(input, window.location.href);
    const safe = new URL(PUBLIC_ORIGIN + (qa ? '/__qa__' : '') + routePath(url.pathname));
    for (const key of Object.keys(UTM)) {
        const value = safeUtm(key, url.searchParams.get(key));
        if (value) safe.searchParams.set(key, value);
    }
    return safe.href;
}

function params() {
    const now = Date.now();
    if (now - acquisition.lastSeen >= 30 * 60 * 1000) {
        acquisition = {landing: routePath(window.location.pathname), utm: {}, lastSeen: now};
        const url = new URL(window.location.href);
        for (const key of Object.keys(UTM)) {
            const value = safeUtm(key, url.searchParams.get(key));
            if (value) acquisition.utm[key] = value;
        }
    }
    acquisition.lastSeen = now;
    storageSet(STORAGE_KEY, JSON.stringify(acquisition));
    return {schema: 1, traffic_type: qa ? 'qa' : 'public', landing: acquisition.landing, ...acquisition.utm};
}

function loadScript(src) {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.referrerPolicy = 'no-referrer';
    script.onload = installHistoryGuard;
    document.head.appendChild(script);
}

function guardLocation(input) {
    const next = cleanBrowserUrl(input == null ? window.location.href : input);
    if (routePath(next.pathname) === '/other') {
        window['ga-disable-' + GA_ID] = true;
        try { window.ym?.(COUNTER_ID, 'destruct'); } catch { /* Navigation must keep working. */ }
        enabled = false;
    }
    syncGoogleLocation(next.href);
    return next;
}

function installHistoryGuard() {
    // Reinstall after tag load too, so a tag's history wrapper receives only the
    // cleaned argument, rather than merely seeing a cleaned location afterwards.
    for (const method of ['pushState', 'replaceState']) {
        const original = window.history[method].bind(window.history);
        window.history[method] = (state, title, input) => original(state, title, guardLocation(input).href);
    }
}

function syncGoogleLocation(url) {
    if (typeof window.gtag === 'function') window.gtag('set', {page_location: pageUrl(url), page_referrer: safeReferrer(document.referrer), page_title: 'Подбор книг'});
}

export function initializeAnalytics() {
    if (initialized) return;
    initialized = true;
    const initial = new URL(window.location.href);
    const qaOption = initial.searchParams.get('analytics_qa');
    if (qaOption === '1') storageSet(QA_KEY, '1');
    if (qaOption === 'off') storageSet(QA_KEY, '0');
    qa = qaOption === '1' || (qaOption !== 'off' && storageGet(QA_KEY) === '1');
    nativeReplace = window.history.replaceState.bind(window.history);
    const clean = cleanBrowserUrl(initial.href);
    nativeReplace(window.history.state, '', clean.href);
    const local = ['localhost', '127.0.0.1', '[::1]'].includes(initial.hostname);
    enabled = (initial.hostname === 'podborknig.ru' && initial.protocol === 'https:') || (local && qa);
    if (routePath(initial.pathname) === '/other') enabled = false;

    // Revalidate cached values; session storage is not a trusted analytics input.
    let saved;
    try { saved = JSON.parse(storageGet(STORAGE_KEY)); } catch { /* Start fresh. */ }
    if (!saved || !Number.isFinite(saved.lastSeen) || Date.now() - saved.lastSeen >= 30 * 60 * 1000 || saved.lastSeen > Date.now()) saved = null;
    const landing = saved && typeof saved.landing === 'string' ? routePath(saved.landing) : routePath(initial.pathname);
    const utm = {};
    for (const key of Object.keys(UTM)) {
        const value = saved ? saved.utm?.[key] : initial.searchParams.get(key);
        if (typeof value === 'string' && value) utm[key] = safeUtm(key, value);
    }
    acquisition = {landing, utm, lastSeen: Date.now()};
    storageSet(STORAGE_KEY, JSON.stringify(acquisition));
    if (!enabled) return;

    // Install before either tag: unknown URL parameters/fragments never reach
    // automatic history pageviews, including on signup/Telegram screens.
    installHistoryGuard();
    window.addEventListener('popstate', () => {
        const clean = guardLocation(window.location.href);
        nativeReplace(window.history.state, '', clean.href);
    }, {capture: true});
    window.ym = window.ym || function () { (window.ym.a = window.ym.a || []).push(arguments); };
    window.ym.l = Date.now();
    window.ym(COUNTER_ID, 'init', {
        defer: true, url: pageUrl(), referrer: safeReferrer(document.referrer),
        accurateTrackBounce: true, clickmap: false, trackLinks: false,
        webvisor: false, trackHash: false, sendTitle: false, disableYtm: true,
        params: {pod33: params()}
    });
    loadScript('https://mc.yandex.ru/metrika/tag.js');

    // GA4 already observes history changes. Do not add manual GA pageviews.
    // Keep local/synthetic QA exclusively in the explicitly labelled Metrika slice.
    if (!qa) {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function () { window.dataLayer.push(arguments); };
        window.gtag('js', new Date());
        window.gtag('config', GA_ID, {page_location: pageUrl(), page_referrer: safeReferrer(document.referrer), page_title: 'Подбор книг', allow_google_signals: false, allow_ad_personalization_signals: false});
        loadScript('https://www.googletagmanager.com/gtag/js?id=' + GA_ID);
    }
}

export function recordPageView() {
    if (!enabled) return;
    const page = routePath(window.location.pathname);
    if (page === '/other' || page === lastPage) return;
    const previous = lastPage ? PUBLIC_ORIGIN + (qa ? '/__qa__' : '') + lastPage : safeReferrer(document.referrer);
    lastPage = page;
    syncGoogleLocation();
    window.ym(COUNTER_ID, 'hit', pageUrl(), {referer: previous, title: 'Подбор книг', params: {pod33: params()}});
}

// A filter reset is not navigation to a new screen. Bypass history listeners
// installed by GA4 while still enforcing the same URL allowlist.
export function replaceFilterUrl(url) {
    const clean = cleanBrowserUrl(url);
    (nativeReplace || window.history.replaceState.bind(window.history))(window.history.state, '', clean.href);
}

export function durationBucket(milliseconds) {
    return milliseconds < 1000 ? 'under_1s' : milliseconds < 3000 ? '1_to_3s' : milliseconds < 10000 ? '3_to_10s' : 'over_10s';
}

export function trackEvent(name, details = {}) {
    if (!enabled || !EVENTS.has(name)) return;
    const data = {...params(), screen: routePath(window.location.pathname)};
    const event = {};
    if (OUTCOMES.has(details.outcome)) event.outcome = details.outcome;
    if (ACTIONS.has(details.action)) event.action = details.action;
    if (['nonempty', 'empty'].includes(details.result_state)) event.result_state = details.result_state;
    if (Number.isInteger(details.result_count) && details.result_count >= 0) event.result_count = Math.min(details.result_count, 10000);
    if (['under_1s', '1_to_3s', '3_to_10s', 'over_10s'].includes(details.duration_bucket)) event.duration_bucket = details.duration_bucket;
    // Session parameters accumulate. Namespacing prevents a catalog success
    // from being mistaken for a successful filter or Telegram action.
    data.events = {[name]: event};
    try { window.ym(COUNTER_ID, 'reachGoal', name, {pod33: data}); } catch { /* Analytics must never break an action. */ }
}
